<template>
	<section>
		<el-row>
			<el-col :sm="12" class="flex flex--aligncenter">
				<h1>Willkommen zurück, {{ user.firstName }}!</h1>
			</el-col>
			<el-col :sm="12" class="flex flex--justifyend button-panel">
				<RouterLink :to="'/user-anlegen'" class="el-button el-button--danger is-round" :title="'Neuen User anlegen'">
					<el-icon><Plus /></el-icon>Neuer User
				</RouterLink>

				<RouterLink :to="'/kontakt-anlegen'" class="el-button el-button--danger is-round" :title="'Neuen Kontakt anlegen'">
					<el-icon><Plus /></el-icon>Neuer Kontakt
				</RouterLink>

				<RouterLink :to="'/beschwerde-anlegen'" class="el-button el-button--danger is-round" :title="'Neue Beschwerde anlegen'">
					<el-icon><Plus /></el-icon>Neue Beschwerde
				</RouterLink>
			</el-col>
		</el-row>
	</section>

	<section>
		<el-row :gutter="20">
			<el-col :sm="16" :xs="24">
				<TheBox :title="'Letzte Aktivitäten'" :count="unreadActivitiesCnt">
					<LastActivitiesTable :activities="userActivities" :loading="activitiesLoading" />
				</TheBox>
			</el-col>

			<el-col :sm="8" :xs="24">
				<TheTodoList />
			</el-col>
		</el-row>
	</section>

	<div class="bg--white">
		<section id="dashfilter">
			<DashFilter :mainfilters="mainfilters" @toggleMainFilter="toggleMainFilter" @toggleSubFilter="toggleSubFilter" />

			<template v-if="mainfilter_active.key == 'complaint'">
				<h2>Beschwerden ({{ $getCountErgebnisseLabel(complaintsTotalCount) }})</h2>
				<ComplaintsTable
					:tableData="filteredComplaints"
					:loading="complaintsTableLoading"
					:totalCount="complaintsTotalCount"
					@loadNextPage="loadNextPage"
				/>
			</template>

			<template v-if="mainfilter_active.key == 'case'">
				<h2>Fälle ({{ $getCountErgebnisseLabel(casesTotalCount) }})</h2>
				<CasesTable :tableData="filteredCases" :loading="caseTableLoading" :totalCount="casesTotalCount" @loadNextPage="loadNextPage" />
			</template>
		</section>
	</div>
</template>

<script lang="ts">
import { searchCases, searchComplaints } from '@/requests/searches';

import { getUserActivities } from '@/requests/users';
import { Plus } from '@element-plus/icons-vue';
import { computed, defineComponent } from 'vue';
import DashFilter, { FilterItem } from '../components/DashFilter.vue';
import TheBox from '../components/TheBox.vue';
import TheTable from '../components/TheTable.vue';
import CasesTable from '../components/tables/CasesTable.vue';
import ComplaintsTable from '../components/tables/ComplaintsTable.vue';
import TheTodoList from '@/components/TheTodoList.vue';
import LastActivitiesTable from '../components/tables/LastActivitiesTable.vue';
import { authUser, useStore } from '../store';
import {
	ActivityResponse,
	CaseFilters,
	CaseResponse,
	CaseState,
	ComplaintFilters,
	ComplaintResponse,
	ComplaintState,
	SearchCaseRequest,
	SearchComplaintRequest,
} from '../types/types';
import { texts } from '../utils/texts';

export default defineComponent({
	name: 'DashboardView',
	components: {
		TheBox,
		TheTodoList,
		TheTable,
		LastActivitiesTable,
		ComplaintsTable,
		CasesTable,
		DashFilter,
		Plus,
	},
	setup() {
		const store = useStore();
		const unreadActivitiesCnt = computed(() => store.state.unreadActivitiesCnt);

		return {
			store,
			unreadActivitiesCnt,
			user: authUser,
		};
	},
	data() {
		return {
			userActivities: [] as ActivityResponse[],
			activitiesLoading: false,
			filteredComplaints: [] as ComplaintResponse[],
			complaintsTotalCount: 0,
			complaintsTableLoading: false,
			filteredCases: [] as CaseResponse[],
			casesTotalCount: 0,
			caseTableLoading: false,
			tablePage: 0,
			itemsPerPage: 14,
			mainfilters: [
				{
					key: 'complaint',
					label: 'Beschwerden',
					active: true,
					subfilters: [
						{ key: ComplaintFilters.NotAssigned, label: 'nicht zugewiesen', active: false, count: 0 },
						{ key: ComplaintFilters.Assigned, label: 'zugewiesen', active: false, count: 0 },
						{ key: ComplaintFilters.UnansweredCallbacks, label: 'mit offener Rückfrage', active: false, count: 0 },
					],
				},
				{
					key: 'case',
					label: 'Fälle',
					active: false,
					subfilters: [
						{ key: CaseFilters.Statements, label: 'Stellungnahme angefordert', active: false, count: 0 },
						{ key: CaseFilters.ExaminationSmallSenate, label: 'im kleinen Senat', active: false, count: 0 },
						{ key: CaseFilters.ExaminationWerberat, label: 'im Werberat', active: false, count: 0 },
						{ key: CaseFilters.DecisionNoReason, label: 'Entscheidung: Kein Grund', active: false, count: 0 },
					],
				},
			] as FilterItem[],
			complaintFilters: [] as any[],
			casesFilters: [] as any[],
		};
	},
	computed: {
		texts() {
			return texts;
		},
		mainfilter_active(): FilterItem {
			return this.mainfilters.find((m) => m.active) || this.mainfilters[0];
		},
	},
	methods: {
		async loadComplaints(filters: ComplaintFilters[] = []) {
			this.complaintsTableLoading = true;

			const dashboardSearch: SearchComplaintRequest = {
				searchTerm: null,
				category: null,
				medium: null,
				fromDate: null,
				toDate: null,
				hasAttachments: null,
				states: [ComplaintState.NotAssigned],
				isPublished: null,
				hasOpenCallbacks: null,
			};

			if (filters.includes(ComplaintFilters.UnansweredCallbacks)) {
				dashboardSearch.hasOpenCallbacks = true;
			}

			if (filters.includes(ComplaintFilters.Assigned) || filters.includes(ComplaintFilters.NotAssigned)) {
				dashboardSearch.states = filters.filter((f) => f !== ComplaintFilters.UnansweredCallbacks).map((f) => f as unknown as ComplaintState);
			} else {
				dashboardSearch.states = null;
			}

			const complaintResult = await searchComplaints(dashboardSearch, this.tablePage, this.itemsPerPage, true);
			if (complaintResult.success) {
				this.complaintsTotalCount = complaintResult.data.totalCount;
				if (this.tablePage === 0) {
					this.filteredComplaints = complaintResult.data.items;
				} else {
					this.filteredComplaints = [...this.filteredComplaints, ...complaintResult.data.items];
				}

				this.mainfilters = this.mainfilters.map((m) => {
					if (m.key === 'complaint') {
						m.subfilters = m.subfilters.map((f) => {
							if (f.key === ComplaintFilters.NotAssigned) {
								f.count = complaintResult.data.counts?.[ComplaintFilters.NotAssigned.toString()] ?? 0;
							} else if (f.key === ComplaintFilters.Assigned) {
								f.count = complaintResult.data.counts?.[ComplaintFilters.Assigned.toString()] ?? 0;
							} else if (f.key === ComplaintFilters.UnansweredCallbacks) {
								f.count = complaintResult.data.counts?.[ComplaintFilters.UnansweredCallbacks.toString()] ?? 0;
							}
							return f;
						});
					}
					return m;
				});
			} else {
				this.filteredComplaints = [];
				this.complaintsTotalCount = 0;
			}

			this.complaintsTableLoading = false;
		},
		async loadCases(filters: CaseFilters[] = []) {
			this.caseTableLoading = true;
			const dashboardSearch: SearchCaseRequest = {
				searchTerm: null,
				fromDate: null,
				toDate: null,
				states: [],
				isPublished: null,
				category: null,
				medium: null,
				reach: null,
				sector: null,
			};

			filters.forEach((f) => {
				if (f === CaseFilters.ExaminationWerberat) {
					dashboardSearch.states!.push(CaseState.ExaminationWerberat);
					dashboardSearch.states!.push(CaseState.ExaminationWerberatRunning);
				} else if (f === CaseFilters.ExaminationSmallSenate) {
					dashboardSearch.states!.push(CaseState.ExaminationSmallSenate);
					dashboardSearch.states!.push(CaseState.ExaminationSmallSenateRunning);
				} else {
					dashboardSearch.states!.push(f as unknown as CaseState);
				}
			});

			const caseResult = await searchCases(dashboardSearch, this.tablePage, 20, true);

			if (caseResult.success) {
				this.casesTotalCount = caseResult.data.totalCount;
				if (this.tablePage === 0) {
					this.filteredCases = caseResult.data.items;
				} else {
					this.filteredCases = [...this.filteredCases, ...caseResult.data.items];
				}

				this.mainfilters = this.mainfilters.map((m) => {
					if (m.key === 'case') {
						m.subfilters = m.subfilters.map((f) => {
							if (f.key === CaseFilters.Statements) {
								f.count = caseResult.data.counts?.[CaseFilters.Statements.toString()] ?? 0;
							} else if (f.key === CaseFilters.ExaminationSmallSenate) {
								f.count = caseResult.data.counts?.['ExaminationSmallSenateRunning'] ?? 0;
							} else if (f.key === CaseFilters.ExaminationWerberat) {
								f.count = caseResult.data.counts?.['ExaminationWerberatRunning'] ?? 0;
							} else if (f.key === CaseFilters.DecisionNoReason) {
								f.count = caseResult.data.counts?.[CaseFilters.DecisionNoReason.toString()] ?? 0;
							}
							return f;
						});
					}
					return m;
				});
			} else {
				this.filteredCases = [];
				this.casesTotalCount = 0;
			}

			this.caseTableLoading = false;
		},
		async loadNextPage() {
			this.tablePage++;
			const filters = this.mainfilter_active.subfilters.filter((f) => f.active).map((f) => f.key);
			if (this.mainfilter_active.key === 'complaint') {
				this.loadComplaints(filters);
			} else if (this.mainfilter_active.key === 'case') {
				this.loadCases(filters);
			}
		},
		async loadActivities() {
			this.activitiesLoading = true;
			const activityResult = await getUserActivities();

			if (activityResult.success) {
				this.userActivities = activityResult.data;
			} else {
				this.userActivities = [];
			}
			this.activitiesLoading = false;
		},
		goToActivityEvent() {},
		toggleMainFilter(filter) {
			this.tablePage = 0;
			this.mainfilters = this.mainfilters.map((m) => {
				// set all mainfilters to false except current one
				return {
					...m,
					active: m.key === filter ? true : false,
				};
			});

			const filters = this.mainfilter_active.subfilters.filter((f) => f.active).map((f) => f.key);
			if (filter === 'complaint') {
				this.loadComplaints(filters);
			} else if (filter === 'case') {
				this.loadCases(filters);
			}
		},
		toggleSubFilter(filter) {
			this.tablePage = 0;
			const findSub = this.mainfilter_active.subfilters.find((f) => f.key === filter);
			if (findSub) {
				findSub.active = !findSub.active;
			}

			if (this.mainfilter_active.key === 'complaint') {
				this.loadComplaints(this.mainfilter_active.subfilters.filter((f) => f.active).map((f) => f.key));
			} else if (this.mainfilter_active.key === 'case') {
				this.loadCases(this.mainfilter_active.subfilters.filter((f) => f.active).map((f) => f.key));
			}
		},
	},
	mounted() {
		this.loadActivities();
		this.loadComplaints();
	},
});
</script>
